/* eslint-disable no-alert */
import cs from 'classnames'
import { HOTKEYS } from 'enums'
import { useHotkeys } from 'react-hotkeys-hook'
import Tooltip from '@material-ui/core/Tooltip'
import { Box } from '@material-ui/core'
import { showCustomizeView, showEditorMainSettings, showLogActions } from 'actions/mainView'
import { ProjectSaveAsDialog } from 'components/Projects/components/ProjectSaveAsDialog/ProjectSaveAsDialog'
import { ProjectExportDialog } from 'components/Projects/components/ProjectExportDialog/ProjectExportDialog'
import { DropdownMenu } from 'components/base/DropdownMenu/DropdownMenu'
import MessageBox from 'components/base/MessageBox'
import Switch from 'components/base/Switch'
import { TranslationContext } from 'contexts/TranslationContext'
import { useAction } from 'hooks'
import { useEditorActions } from 'hooks/useEditorActions'
import React, { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { activeHotkeyProfileSelector, userProfileSelector } from '~/selectors/user-profile-selector'
import { ReactComponent as BurgerIcon } from '~/assets/icons/burger.svg'
import './EditorMenu.scss'
import { browseFolder } from '~/Util'
import FolderIcon from '~/assets/icons/folder-open.svg'
import { selectLogPath, selectLogsEnabled } from '~/selectors/app'
import { selectShowEditorSettings } from '~/selectors/mainView'
import { selectAdobeCepExtentions } from '~/selectors/adobeCepExtentions'
import { openCepExtension } from '~/actions/adobeCepExtentions'
import { selectActiveProjectId, selectCanModifyActiveProject } from '~/selectors/projectData'
import { AppRoutes } from '~/AppRoutes'


export const EditorMenu = () => {
  const { onClickNewProject, saveAsDialog, exportProjectDialog, activeProjectName, onPublish,
    isRenderingSupported, isNewProject, isAskProjectName } = useEditorActions()
  const customizeEditor = useAction(showCustomizeView, 'theme')
  const customizeHotkeys = useAction(showCustomizeView, 'hotkeys')
  const onEditorSettings = useAction(showEditorMainSettings)
  const onShowLogActions = useAction(showLogActions)
  const activeHotkeyProfile = useSelector(activeHotkeyProfileSelector)
  const [ errorModal, setErrorModal ] = React.useState(false)
  const logPath = useSelector(selectLogPath)
  const logEnabled = useSelector(selectLogsEnabled)
  const isShowSettings = useSelector(selectShowEditorSettings)
  const { userId, apiKey, admin } = useSelector(userProfileSelector)
  const adobeCepExtentions = useSelector(selectAdobeCepExtentions)
  const showCustomization = userId === apiKey
  const canModify = useSelector(state => selectCanModifyActiveProject(state))
  const activeProjectId = useSelector(selectActiveProjectId)
  const { t } = React.useContext(TranslationContext)
  const dispatch = useDispatch()

  const launchExtension = React.useCallback(ext => {
    dispatch(openCepExtension(ext))
  }, [ dispatch ])

  const projectMenuItems = React.useMemo(() => {
    const items = [
      {
        text: t('EDITOR_MENU_LIST_ITEM_NEW_PROJECT'),
        onClick: () => {
          onClickNewProject()
        },
        visible: __CFG__.EDITOR_MENU.NEW_PROJECT,
      },
      {
        text: t('EDITOR_MENU_LIST_ITEM_SAVE_PROJECT_AS'),
        onClick: () => {
          saveAsDialog.open()
        },
        visible: canModify && __CFG__.EDITOR_MENU.SAVE_PROJECT_AS,
      }]
    items.push({
      text: t('EDITOR_MENU_LIST_ITEM_PUBLISH'),
      onClick: () => {
        if (isRenderingSupported) {
          onPublish()
        } else {
          setErrorModal(true)
        }
      },
      visible: __CFG__.EDITOR_MENU.ITEM_PUBLISH,
    })
    items.push({
      text: t('PROJECTS_EXPORT_TITLE'),
      onClick: () => {
        exportProjectDialog.open()
      },
      visible: admin,
    })
    if (adobeCepExtentions.length > 0) {
      items.push({ divider: true, visible: true })

      items.push({
        text: t('EDITOR_MENU_ADOBE_CEP_EXTENTIONS'),
        disabled: true,
        visible: true,
      })

      items.push(
        ...adobeCepExtentions.map(ext => ({
          text: `  ${ext.menu}`,
          onClick: () => launchExtension(ext),
          visible: true,
        }))
      )
    }
    items.push(
      {
        divider: true,
        visible: __CFG__.EDITOR_MENU.SETTINGS_HOTKEYS,
      },
      {
        text: t('SETTINGS_HOTKEYS'),
        onClick: customizeHotkeys,
        visible: __CFG__.EDITOR_MENU.SETTINGS_HOTKEYS,
      }
    )
    items.push({
      text: t('EDITOR_MENU_LIST_ITEM_CUSTOMIZE_EDITOR'),
      onClick: customizeEditor,
      visible: showCustomization && __CFG__.EDITOR_MENU.CUSTOMIZE_EDITOR,
    })
    items.push({
      text: t('EDITOR_MENU_MAIN_SETTINGS'),
      onClick: () => onEditorSettings(true),
      visible: __CFG__.EDITOR_MENU.MAIN_SETTINGS,
    })
    items.push({
      text: t('EDITOR_MENU_LIST_ITEM_ADMIN_PANEL'),
      onClick: () => {
        window.open(`${window.location.origin}${AppRoutes.AdminPanel}`, '_blank')
      },
      visible: admin,
    })

    items.push(
      {
        divider: true,
        visible: __CFG__.EDITOR_MENU.MAIN_LOGS,
      },
      {
        text: (
          <Box display="flex" width="100%" justifyContent="space-between" alignItems="center" gridGap="8px">
            <Box display="flex" justifyContent="center" alignItems="center" gridGap="8px">
              <Switch
                id={t('EDITOR_MENU_MAIN_LOGS')}
                onChange={_ => onShowLogActions(true)}
                checked={logEnabled}
              />
              {t('EDITOR_MENU_MAIN_LOGS')}
            </Box>
            <img src={FolderIcon} width={26} height={18} alt="folder" onClick={() => browseFolder(logPath)} />
          </Box>
        ),
        // onClick: customizeEditor,
        visible: __CFG__.EDITOR_MENU.MAIN_LOGS,
      }
    )
    return items.filter(item => item.visible)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showCustomization,
    isRenderingSupported,
    isAskProjectName,
    adobeCepExtentions,
    canModify,
    admin,
  ])

  const toggleSettings = useCallback(() => {
    onEditorSettings(!isShowSettings)
  }, [ isShowSettings, onEditorSettings ])

  useHotkeys(activeHotkeyProfile.hotkeys[HOTKEYS.TOGGLE_SETTINGS_VISIBLE], toggleSettings, {
    enableOnFormTags: true,
    preventDefault: true,
  })

  return (
    <>
      <DropdownMenu
        className={cs('editor_menu')}
        hideChevron
        emitFromContent
        menuItems={projectMenuItems}
      >
        <Tooltip title="Editor Menu">
          <BurgerIcon />
        </Tooltip>
      </DropdownMenu>

      <ProjectSaveAsDialog
        modalDialog={saveAsDialog}
        projectName={activeProjectName}
        isNewProject={isNewProject}
      />
      <ProjectExportDialog
        modalDialog={exportProjectDialog}
        projectId={activeProjectId}
      />
      {errorModal && (
        <MessageBox
          title={t('MESSAGE_BOX_UNABLE_TO_PUBLISH')}
          message={t('MESSAGE_BOX_VIDEO_ASSET_REQUIRED')}
          onClose={() => setErrorModal(false)}
        />
      )}
    </>
  )
}
